import React, { useEffect, useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  useMediaQuery,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Fade,
  Slide,
} from '@mui/material';
import {
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Receipt,
} from '@mui/icons-material';
import { useMpesaContext } from '../context/AppContext';
import SummaryCards from '../components/Dashboard/SummaryCards';
import Charts from '../components/Dashboard/Charts';
import RecentTransactions from '../components/Dashboard/RecentTransactions';
import { getTransactions } from '../api';
import axios from 'axios';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const { summary, loading, fetchSummary } = useMpesaContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [recentTransactions, setRecentTransactions] = useState([]);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const token = localStorage.getItem("token");

  const API_BASE_URL = process.env.REACT_APP_API_URL + '/sync';

  useEffect(() => {
    const fetchRecentTransactions = async () => {
      try {
        const response = await getTransactions({ limit: isMobile ? 5 : 10 });
        setRecentTransactions(response.data);
      } catch (error) {
        toast.error('Failed to load recent transactions');
      }
    };
    fetchRecentTransactions();
    fetchSyncStatus();
  }, [isMobile]);

  const fetchSyncStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/sync/status`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });
      setSyncStatus(response.data.data);
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/sync`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        });
      const data = await response.json();
      setSyncResult(data);

      if (data.success) {
        toast.success(data.message || 'Sync completed successfully');
        setSnackbarMessage(data.message || 'Sync completed successfully');
        setSnackbarSeverity('success');
      } else {
        toast.error(data.message || 'Sync failed');
        setSnackbarMessage(data.message || 'Sync failed');
        setSnackbarSeverity('error');
      }
      setSnackbarOpen(true);

      // Refresh data
      await fetchSummary();
      await fetchSyncStatus();

      // Close dialog after 3 seconds
      setTimeout(() => {
        setSyncDialogOpen(false);
        setSyncResult(null);
      }, 3000);

    } catch (error) {
      console.error('Sync error:', error);
      toast.error(error.message || 'Failed to sync transactions');
      setSnackbarMessage(error.message || 'Failed to sync transactions');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setSyncResult({
        success: false,
        message: error.message || 'Failed to sync transactions'
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Quick stats for mobile view
  const QuickStats = () => {
    const stats = summary?.summary || {};
    const statItems = [
      {
        label: 'Total Transactions',
        value: stats.total_transactions || 0,
        icon: <Receipt sx={{ fontSize: 20 }} />,
        color: theme.palette.primary.main,
      },
      {
        label: 'Paid In',
        value: `KES ${(stats.total_paid_in || 0).toLocaleString()}`,
        icon: <TrendingUp sx={{ fontSize: 20 }} />,
        color: theme.palette.success.main,
      },
      {
        label: 'Withdrawn',
        value: `KES ${(stats.total_withdrawn || 0).toLocaleString()}`,
        icon: <TrendingDown sx={{ fontSize: 20 }} />,
        color: theme.palette.error.main,
      },
    ];

    return (
      <Grid container spacing={1} sx={{ mb: 2 }}>
        {statItems.map((item, index) => (
          <Grid item xs={4} key={index}>
            <Card sx={{ textAlign: 'center', p: 1.5, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                <Box sx={{ color: item.color }}>{item.icon}</Box>
                <Typography variant="caption" color="text.secondary">
                  {item.label}
                </Typography>
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {item.value}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header Section */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: 3,
        gap: { xs: 2, sm: 0 }
      }}>
        <Box>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            gutterBottom
            sx={{ color: theme.palette.text.primary }}
          >
            M-Pesa Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Overview of your M-Pesa transactions
          </Typography>
        </Box>

        {/* Sync Button - Responsive */}
        <Button
          variant="contained"
          color="primary"
          startIcon={<SyncIcon />}
          onClick={() => setSyncDialogOpen(true)}
          fullWidth={isMobile}
          sx={{
            backgroundColor: theme.palette.success.main,
            '&:hover': {
              backgroundColor: theme.palette.success.dark,
            },
            borderRadius: 2,
            py: { xs: 1.5, sm: 1 },
            fontSize: { xs: '0.875rem', sm: '0.9375rem' },
          }}
        >
          {isMobile ? 'Sync' : 'Sync to Expenses'}
          {syncStatus?.pending > 0 && (
            <Chip
              label={syncStatus.pending}
              size="small"
              sx={{
                ml: 1,
                backgroundColor: theme.palette.error.main,
                color: 'white',
                fontSize: '0.7rem',
                height: 20,
              }}
            />
          )}
        </Button>
      </Box>

      {/* Sync Status Bar - Responsive */}
      {syncStatus && (
        <Paper sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 3,
          bgcolor: theme.palette.info.light + '20',
          borderRadius: 2,
        }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Total: <strong>{syncStatus.total}</strong>
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Synced: <strong style={{ color: theme.palette.success.main }}>{syncStatus.synced}</strong>
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Pending: <strong style={{ color: theme.palette.warning.main }}>{syncStatus.pending}</strong>
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Quick Stats for Mobile */}
      {isMobile && <QuickStats />}

      {/* Summary Cards - Responsive */}
      <SummaryCards summary={summary} loading={loading} />

      {/* Charts and Recent Transactions - Responsive Grid */}
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} lg={8}>
          <Paper
            sx={{
              p: { xs: 1.5, sm: 2, md: 3 },
              height: { xs: '300px', sm: '350px', md: '400px' },
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              boxShadow: theme.shadows[1],
              position: 'relative',
            }}
          >
            <Charts summary={summary} />
          </Paper>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Paper
            sx={{
              p: { xs: 1.5, sm: 2, md: 3 },
              height: { xs: '300px', sm: '350px', md: '400px' },
              overflow: 'auto',
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              boxShadow: theme.shadows[1],
            }}
          >
            <RecentTransactions transactions={recentTransactions} />
          </Paper>
        </Grid>
      </Grid>

      {/* Sync Confirmation Dialog - Responsive */}
      <Dialog
        open={syncDialogOpen}
        onClose={() => !syncing && setSyncDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={isMobile ? Slide : Fade}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 2 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100vh', sm: '90vh' },
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}>
          <Typography variant={isMobile ? 'h6' : 'h5'}>
            {syncResult?.success ? 'Sync Completed' : 'Sync Transactions'}
          </Typography>
          {isMobile && (
            <IconButton onClick={() => setSyncDialogOpen(false)} disabled={syncing}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          {syncResult ? (
            // Show sync result
            <Box>
              <Alert
                severity={syncResult.success ? 'success' : 'error'}
                icon={syncResult.success ? <CheckCircleIcon /> : <WarningIcon />}
                sx={{ borderRadius: 2 }}
              >
                <Typography variant="body1" gutterBottom>
                  {syncResult.message}
                </Typography>
                {syncResult.success && syncResult.data && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Synced: {syncResult.data?.synced || 0} transactions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Skipped: {syncResult.data?.skipped || 0} transactions
                    </Typography>
                    {syncResult.data?.details && (
                      <Box sx={{ mt: 2, maxHeight: 150, overflow: 'auto' }}>
                        <Typography variant="caption" display="block" color="text.secondary">
                          Recently synced:
                        </Typography>
                        {syncResult.data.details.slice(0, 5).map((item, index) => (
                          <Typography key={index} variant="caption" display="block" sx={{ fontSize: '11px' }}>
                            {item.receipt_no} → Expense #{item.expense_id}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </Alert>
            </Box>
          ) : (
            // Show confirmation
            <Box>
              <Typography variant="body1" gutterBottom>
                This will sync all unsynced M-Pesa transactions to the expenses table.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Total pending: <strong>{syncStatus?.pending || 0}</strong> transactions
              </Typography>
              <Box sx={{
                mt: 2,
                p: { xs: 1.5, sm: 2 },
                bgcolor: theme.palette.warning.light + '20',
                borderRadius: 1,
                border: `1px solid ${theme.palette.warning.light}`,
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <WarningIcon sx={{ fontSize: 16, mr: 1, mt: 0.2, flexShrink: 0 }} />
                  <span>Only withdrawals will be synced as expenses. Credits will be skipped.</span>
                </Typography>
              </Box>
              {isMobile && syncStatus?.pending > 0 && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: theme.palette.primary.light + '10', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="flex" alignItems="center">
                    <InfoIcon sx={{ fontSize: 14, mr: 1 }} />
                    <span>This will process {syncStatus.pending} transactions</span>
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{
          p: { xs: 2, sm: 2.5 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}>
          {!syncResult ? (
            <>
              <Button
                onClick={() => setSyncDialogOpen(false)}
                disabled={syncing}
                fullWidth={isMobile}
                variant={isMobile ? 'outlined' : 'text'}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSync}
                variant="contained"
                color="primary"
                disabled={syncing || (syncStatus?.pending === 0)}
                startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                fullWidth={isMobile}
                sx={{
                  backgroundColor: theme.palette.success.main,
                  '&:hover': {
                    backgroundColor: theme.palette.success.dark,
                  },
                }}
              >
                {syncing ? 'Syncing...' : `Sync ${syncStatus?.pending || 0} Transactions`}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                setSyncDialogOpen(false);
                setSyncResult(null);
              }}
              variant="contained"
              color="primary"
              fullWidth={isMobile}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: isMobile ? 'top' : 'bottom',
          horizontal: 'center'
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            borderRadius: 2,
            boxShadow: theme.shadows[3],
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;